import { increment } from "firebase/firestore";
import { EventSentToMobileApp, type DeckObject, type UserData } from "../../constants/GlobalTypesAndEnumsAndConstants";
import {
  navigationWithStatesStore,
  pageStore,
  type DesktopNotificationsOptInScreenContext,
  type UpcomingExamForNotificationCopy,
} from "../../zustand/globalStore";
import { updateUser } from "../models/UserModel";
import { getDaysUntilExam } from "../models/ExamModel";
import { isDesktop } from "./detect-device";
import { getLatestThreeExamsSortedByDateForUser } from "./helper-methods";
import { serializeMobileAppEvent } from "./mobile-app-events";
import { trackError } from "../../utils/analytics/track-error";

// When the app asks a learner to turn on notifications: only at a moment that gives a reminder a reason (an
// exam date was just saved, a review was just finished), at most three times per account, a week apart at
// least. Asking with no context (on a visit count, at the start of a review, the browser's own prompt on the
// home page) taught learners to say no.
export const MAXIMUM_NOTIFICATION_PERMISSION_ASKS_PER_ACCOUNT = 3;
export const MINIMUM_DAYS_BETWEEN_NOTIFICATION_PERMISSION_ASKS = 7;
// The ask waits this long for the learner's next exam, then goes ahead with the copy that names no exam.
export const MAXIMUM_WAIT_FOR_THE_UPCOMING_EXAM_MILLISECONDS = 3_000;

const MILLISECONDS_IN_A_DAY = 24 * 60 * 60 * 1000;

export enum NotificationPermissionAskMoment {
  ExamDateSaved = "after saving an exam date",
  ReviewFinished = "after finishing a review",
}

export enum NotificationPermissionAskChannel {
  // `review_reminder` to the mobile app, which shows its own screen and then the phone's prompt
  MobileApp = "mobile_app",
  // DesktopNotificationsOptInScreen, whose button opens the browser's prompt
  DesktopBrowser = "desktop_browser",
}

export type NotificationPermissionAskCircumstances = {
  user: UserData | null | undefined;
  isRunningAsMobileApp: boolean;
  isDesktop: boolean;
  // null when the browser has no notifications at all
  browserNotificationPermission: NotificationPermission | null;
  // the existing once-per-review-session guard (AppRouter clears it when a review opens)
  hasAlreadyAskedInThisReviewSession: boolean;
  now: Date;
};

/** The exam a learner has just saved, which the ask then leads with. */
export type SavedExamForNotificationCopy = {
  examName: string | null | undefined;
  dateOfExam: Date;
};

function readDateOfLastAsk(storedDateOfLastAsk: unknown): Date | null {
  let dateOfLastAsk: Date | null = null;
  if (storedDateOfLastAsk instanceof Date) {
    dateOfLastAsk = storedDateOfLastAsk;
  } else if (typeof storedDateOfLastAsk === "string" || typeof storedDateOfLastAsk === "number") {
    dateOfLastAsk = new Date(storedDateOfLastAsk);
  } else if (
    storedDateOfLastAsk &&
    typeof storedDateOfLastAsk === "object" &&
    typeof (storedDateOfLastAsk as { seconds?: unknown }).seconds === "number"
  ) {
    // a Firestore Timestamp, as the user document returns it
    dateOfLastAsk = new Date((storedDateOfLastAsk as { seconds: number }).seconds * 1000);
  }
  return dateOfLastAsk && !Number.isNaN(dateOfLastAsk.getTime()) ? dateOfLastAsk : null;
}

/** Where to ask, or null when it is not the time to ask this learner. */
export function getNotificationPermissionAskChannel(
  circumstances: NotificationPermissionAskCircumstances
): NotificationPermissionAskChannel | null {
  const { user } = circumstances;
  if (!user?.id || circumstances.hasAlreadyAskedInThisReviewSession) return null;

  const numberOfAsksSoFar = user.user_settings?.notification_permission_prompt_count;
  if (typeof numberOfAsksSoFar === "number" && numberOfAsksSoFar >= MAXIMUM_NOTIFICATION_PERMISSION_ASKS_PER_ACCOUNT) {
    return null;
  }

  const dateOfLastAsk = readDateOfLastAsk(user.user_settings?.date_last_notification_permission_prompt);
  const minimumTimeBetweenAsksInMilliseconds =
    MINIMUM_DAYS_BETWEEN_NOTIFICATION_PERMISSION_ASKS * MILLISECONDS_IN_A_DAY;
  if (dateOfLastAsk && circumstances.now.getTime() - dateOfLastAsk.getTime() < minimumTimeBetweenAsksInMilliseconds) {
    return null;
  }

  if (circumstances.isRunningAsMobileApp) {
    return user.user_settings?.did_user_grant_push_notification_permissions === true
      ? null
      : NotificationPermissionAskChannel.MobileApp;
  }
  // Only "default" can still be asked: after "denied" the browser no longer shows its prompt, and the screen
  // could only send the learner to the browser's settings.
  if (circumstances.isDesktop && circumstances.browserNotificationPermission === "default") {
    return NotificationPermissionAskChannel.DesktopBrowser;
  }
  return null;
}

type ExamDate = Parameters<typeof getDaysUntilExam>[0];

// An exam today (0 days) or past has nothing left to give a heads-up for.
function getUpcomingExamForCopy(
  examName: string | null | undefined,
  dateOfExam: ExamDate | undefined,
  user: UserData
): UpcomingExamForNotificationCopy | null {
  const trimmedExamName = typeof examName === "string" ? examName.trim() : "";
  if (!trimmedExamName || !dateOfExam) return null;
  const daysUntilExam = getDaysUntilExam(dateOfExam, user);
  return daysUntilExam >= 1 ? { examName: trimmedExamName, daysUntilExam } : null;
}

/**
 * The exam's name in the opt-in copy: the name the learner gave it, which is the name the exam countdown push
 * itself uses, so the example notification shows what will arrive. Legacy exams saved without a name go by
 * their class.
 */
export function getExamNameForNotificationCopy(
  examName: string | null | undefined,
  nameOfTheClassOfTheExam: string | null | undefined
): string {
  const trimmedExamName = (examName ?? "").trim();
  if (trimmedExamName) return trimmedExamName;
  return (nameOfTheClassOfTheExam ?? "").trim();
}

/**
 * The class an exam saved for this set belongs to: the set's top folder (the Worker files the exam under the
 * set's root deck). Known when the set has no parent, or when its chain of parents is loaded; null otherwise.
 */
export function getNameOfTheClassOfDeck(deck: DeckObject | null | undefined): string | null {
  if (!deck) return null;
  if (!deck.parent_deck_id) return deck.name ?? null;
  return deck.parent_decks?.find((parentDeck) => !parentDeck?.parent_deck_id)?.name ?? null;
}

/** The learner's nearest exam that is a day or more away, named for the opt-in copy. */
export async function findTheNextExamForNotificationCopy(
  user: UserData
): Promise<UpcomingExamForNotificationCopy | null> {
  const upcomingExams = await getLatestThreeExamsSortedByDateForUser(user.id, user);
  for (const upcomingExam of upcomingExams) {
    // The class's name as the exam stored it: the helper's `class_name` can be one of the exam's sets instead,
    // when the class folder holds no questions of its own.
    const examNameForNotificationCopy = getExamNameForNotificationCopy(
      upcomingExam.exam_name,
      upcomingExam.deck_name_of_class_to_add_exam_to ?? upcomingExam.class_name
    );
    const upcomingExamForCopy = getUpcomingExamForCopy(examNameForNotificationCopy, upcomingExam.date_of_exam, user);
    if (upcomingExamForCopy) return upcomingExamForCopy;
  }
  return null;
}

function findTheNextExamWithinTheWait(user: UserData): Promise<UpcomingExamForNotificationCopy | null> {
  return new Promise((resolve) => {
    const maximumWaitTimeoutId = setTimeout(() => resolve(null), MAXIMUM_WAIT_FOR_THE_UPCOMING_EXAM_MILLISECONDS);
    findTheNextExamForNotificationCopy(user)
      .catch(() => null)
      .then((upcomingExam) => {
        clearTimeout(maximumWaitTimeoutId);
        resolve(upcomingExam);
      });
  });
}

// One write per ask: the count and the date together.
function recordTheAsk(user: UserData) {
  navigationWithStatesStore.getState().setDidShowNotificationOptInScreenForCurrentNumberOfReviewSessionsStarted(true);
  void updateUser(user.id, {
    user_settings: {
      notification_permission_prompt_count: increment(1),
      date_last_notification_permission_prompt: new Date(),
    },
  });
}

// Straight to the WebView bridge, the channel `sendEventsToMobileApp` uses, so any screen can ask.
function sendReviewReminderToMobileApp(upcomingExam: UpcomingExamForNotificationCopy | null, user: UserData) {
  // The app shows its screen with or without an exam (versions before this message ignore it).
  const reviewReminderMessage = JSON.stringify({
    examName: upcomingExam?.examName ?? null,
    daysUntilExam: upcomingExam?.daysUntilExam ?? null,
  });
  try {
    window.ReactNativeWebView?.postMessage(
      serializeMobileAppEvent(EventSentToMobileApp.REVIEW_REMINDER, reviewReminderMessage)
    );
  } catch (error) {
    trackError(error, {
      scope: "mobile-app-bridge",
      userId: user.id,
      tags: { event_type: EventSentToMobileApp.REVIEW_REMINDER },
    });
  }
}

type NotificationPermissionAskRequest = {
  user: UserData | null | undefined;
  savedExam?: SavedExamForNotificationCopy;
};

/**
 * Asks for notification permission when the policy says it is time: shows the desktop opt-in screen, or tells
 * the mobile app to show its own. Leads with the exam the learner has just saved, or else their next exam.
 * Returns whether it asked.
 */
export function askForNotificationPermissionIfItIsTime(
  moment: NotificationPermissionAskMoment,
  { user, savedExam }: NotificationPermissionAskRequest
): boolean {
  const askChannel = getNotificationPermissionAskChannel({
    user,
    isRunningAsMobileApp: pageStore.getState().isRunningAsMobileApp,
    isDesktop,
    browserNotificationPermission:
      typeof window !== "undefined" && "Notification" in window ? window.Notification.permission : null,
    hasAlreadyAskedInThisReviewSession:
      navigationWithStatesStore.getState().didShowNotificationOptInScreenForCurrentNumberOfReviewSessionsStarted,
    now: new Date(),
  });
  if (!askChannel || !user) return false;

  const savedExamForCopy = savedExam ? getUpcomingExamForCopy(savedExam.examName, savedExam.dateOfExam, user) : null;

  if (askChannel === NotificationPermissionAskChannel.MobileApp) {
    recordTheAsk(user);
    if (savedExamForCopy) {
      sendReviewReminderToMobileApp(savedExamForCopy, user);
    } else {
      void findTheNextExamWithinTheWait(user).then((upcomingExam) => sendReviewReminderToMobileApp(upcomingExam, user));
    }
    return true;
  }

  const desktopNotificationsOptInScreenContext: DesktopNotificationsOptInScreenContext = {
    whyWereShowingThisScreen: moment,
    // undefined: the screen waits (with its spinner) for the lookup below
    upcomingExam: savedExamForCopy ?? undefined,
  };
  navigationWithStatesStore
    .getState()
    .setDesktopNotificationsOptInScreenContext(desktopNotificationsOptInScreenContext);
  navigationWithStatesStore.getState().setShowDesktopNotificationsOptInScreen(true);
  // The store does not show the screen in a private window (notifications do not outlast it): no ask then.
  if (!navigationWithStatesStore.getState().showDesktopNotificationsOptInScreen) {
    navigationWithStatesStore.getState().setDesktopNotificationsOptInScreenContext(null);
    return false;
  }
  recordTheAsk(user);

  if (!savedExamForCopy) {
    void findTheNextExamWithinTheWait(user).then((upcomingExam) => {
      // Only for the screen this ask opened: it may have been closed, or another ask may have opened it again.
      const navigationWithStatesState = navigationWithStatesStore.getState();
      if (navigationWithStatesState.desktopNotificationsOptInScreenContext !== desktopNotificationsOptInScreenContext) {
        return;
      }
      navigationWithStatesState.setDesktopNotificationsOptInScreenContext({
        ...desktopNotificationsOptInScreenContext,
        upcomingExam,
      });
    });
  }
  return true;
}
